import {
  IAIWorkspace,
  useGetAiWorkspaceRequest,
  useReanalyzeCreatorFitRequest,
  useUpdateVetRequestPrompt,
} from "@entities/aiWorkspace";
import React, { useEffect, useState } from "react";
import { useLocalRequestPrompt } from "../useLocalRequestPrompt";
import { CreatorFitCreators } from "./CreatorFitCreators";
import { CreatorFitCriteria } from "./CreatorFitCriteria";
import { CreatorFitIntro } from "./CreatorFitIntro";
import styles from "./creatorFitFlow.module.scss";
import { useCreatorFitActions } from "./useCreatorFitActions";
import { useSingleCreatorFitRequest } from "./useSingleCreatorFitRequest";
import { UploadSheetModal } from "./UploadSheetModal";

function plainPrompt(html: string) {
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export const CreatorFitTab: React.FC<{
  workspace: IAIWorkspace;
  requestId?: string;
  onOpenRequest?: (id: string, type: string) => void;
  onCloseRequest?: () => void;
}> = ({ workspace, requestId, onOpenRequest }) => {
  const { activeId, isResolving } = useSingleCreatorFitRequest(
    workspace._id,
    requestId,
    onOpenRequest
  );
  const [saveVersion, setSaveVersion] = useState(0);
  const [committedPrompt, setCommittedPrompt] = useState("");
  const [reanalyzingId, setReanalyzingId] = useState<string | null>(null);

  const { update: updateRequestPrompt, isUpdating } = useUpdateVetRequestPrompt({
    onSuccess: (prompt) => {
      setCommittedPrompt(prompt);
      setSaveVersion((version) => version + 1);
    },
  });
  const { reanalyze, isReanalyzing } = useReanalyzeCreatorFitRequest(() => {
    setReanalyzingId(null);
  });
  const { data: requestData, isLoading } = useGetAiWorkspaceRequest(activeId);
  const checks = requestData?.data?.creatorFitChecks || [];

  useEffect(() => {
    setCommittedPrompt("");
  }, [activeId]);

  const sourcePrompt = activeId ? requestData?.data?.masterPrompt || "" : "";
  const { promptHtml, initialHtml, updatePrompt, contentKey } =
    useLocalRequestPrompt(workspace._id, "creator-fit", sourcePrompt, activeId);
  const savedRequestPrompt = activeId
    ? committedPrompt || requestData?.data?.masterPrompt || ""
    : undefined;
  const currentPrompt = plainPrompt(promptHtml);
  const canAdd = currentPrompt.length > 0;
  const promptChanged =
    Boolean(activeId) &&
    canAdd &&
    currentPrompt !== plainPrompt(savedRequestPrompt || "");

  const {
    sheetOpen,
    setSheetOpen,
    isCreating,
    isAddingCreator,
    isRerunningAll,
    addCreators,
    rerunAll,
  } = useCreatorFitActions({
    workspaceId: workspace._id,
    activeId,
    promptHtml,
    promptChanged,
    checks,
    onOpenRequest,
    onPromptSaved: (prompt) => {
      setCommittedPrompt(prompt);
      setSaveVersion((version) => version + 1);
    },
  });

  const creatorsSectionRef = React.useRef<HTMLDivElement>(null);

  const scrollToCreators = () => {
    window.setTimeout(() => {
      creatorsSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const handleAddCreator: typeof addCreators = (creator) => {
    if (!canAdd) return;
    addCreators(creator);
    scrollToCreators();
  };

  const progressStep = !canAdd ? 1 : checks.length === 0 ? 2 : 3;

  return (
    <div className={styles.flow}>
      <CreatorFitIntro current={progressStep} />
      <CreatorFitCriteria
        workspaceId={workspace._id}
        requestId={activeId}
        initialHtml={initialHtml}
        savedRequestPrompt={savedRequestPrompt}
        contentKey={contentKey}
        isSaving={isUpdating}
        saveVersion={saveVersion}
        onHtmlChange={updatePrompt}
        onSave={
          activeId
            ? (prompt) =>
                updateRequestPrompt({ aiWorkspaceRequestId: activeId, prompt })
            : undefined
        }
        showStale={promptChanged && checks.length > 0}
        isRerunningAll={isRerunningAll}
        onRerunAll={() => {
          void rerunAll();
          scrollToCreators();
        }}
      />

      <div ref={creatorsSectionRef}>
        <CreatorFitCreators
          checks={checks}
          canAdd={canAdd}
          busy={isCreating || isAddingCreator || isResolving || isLoading}
          onAdd={handleAddCreator}
          onOpenSheet={() => setSheetOpen(true)}
          requestMasterPromptId={requestData?.data?.masterPromptId}
          promptChanged={promptChanged}
          reanalyzingId={reanalyzingId}
          isReanalyzing={isReanalyzing}
          onReanalyze={(checkId) => {
            if (!activeId) return;
            setReanalyzingId(checkId);
            reanalyze({
              aiWorkspaceRequestId: activeId,
              creatorFitCheckId: checkId,
            });
            scrollToCreators();
          }}
        />
      </div>

      <UploadSheetModal
        isOpen={sheetOpen}
        onClose={() => setSheetOpen(false)}
        onImport={handleAddCreator}
        isImporting={isCreating || isAddingCreator}
        existingUsernames={
          checks
            .map((check) => check.socialMedia?.username?.toLowerCase())
            .filter(Boolean) as string[]
        }
      />
    </div>
  );
};
